import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import {
    ForecastValue,
    ForecastValueType
} from '../../components/ForecastValue/ForecastValue';
import { GridCampaignNameCell } from '../../components/GridCampaignNameCell/GridCampaignNameCell';
import { GridGroupNameCell } from '../../components/GridGroupNameCell/GridGroupNameCell';
import { MoneyCell } from '../../components/MoneyCell/MoneyCell';
import { MoneyText } from '../../components/MoneyText/MoneyText';
import { NumberCell } from '../../components/NumberCell/NumberCell';
import { NumberText } from '../../components/NumberText/NumberText';
import { RecommendationsGridItemAllowCell } from '../../components/RecommendationsGridItemAllowCell/RecommendationsGridItemAllowCell';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { ColumnsSettings, ColumnsSettingsViewDict } from '../../typings/common';
import {
    CurrencyCode,
    GdRecommendationStatus
} from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import {
    RecommendationsItemsScreenActions,
    RecommendationType,
    RemovePagesFromBlackListRecommendationsItem,
    SwitchOnAutotargetingRecommendationsItem
} from '../../typings/recommendations';
import { StatKey } from '../stats/stats';

import * as keyset from './switchOnAutotargetingRecommendations.i18n';

const i18n = i18nFactory(keyset);

export enum SwitchOnAutotargetingKey {
    CAMPAIGN_ID = 'campaign_id',
    NAME = 'NAME',
    CLICKS_FORECAST = 'CLICKS_FORECAST',
    COST_FORECAST = 'COST_FORECAST',
    ACCEPT = 'ACCEPT'
}

export function getStaticSwitchOnAutotargetingRecommendationsColumns(
    actions: RecommendationsItemsScreenActions,
    login: string,
    currency: CurrencyCode
): Array<
    OptionalGridColumnDecl<
        | SwitchOnAutotargetingRecommendationsItem
        | RemovePagesFromBlackListRecommendationsItem,
        {},
        GridColumnsOptions
    >
> {
    return [
        {
            title: i18n('campaign'),
            key: SwitchOnAutotargetingKey.CAMPAIGN_ID,
            defaultSortDirection: SortDirection.asc,
            fixed: true,
            mandatory: true,
            minWidth: 150,
            CellComponent: React.memo(
                ({
                    rowData: { campaign }
                }: GridCellProps<
                    SwitchOnAutotargetingRecommendationsItem
                >): JSX.Element => {
                    const { id, name } = campaign;

                    return (
                        <GridCampaignNameCell key={id} name={name} id={id} />
                    );
                }
            )
        },
        {
            title: i18n('name'),
            key: SwitchOnAutotargetingKey.NAME,
            fixed: true,
            isSortable: true,
            defaultSortDirection: SortDirection.asc,
            minWidth: 140,
            CellComponent: React.memo(
                ({
                    rowData: { name, id }
                }: GridCellProps<
                    SwitchOnAutotargetingRecommendationsItem
                >): JSX.Element => (
                    <GridGroupNameCell
                        name={name}
                        id={id}
                        isCompactViewEnabled={false}
                    />
                )
            )
        },
        {
            title: i18n('clicks'),
            key: StatKey.STAT_CLICKS,
            align: 'right',
            isSortable: true,
            minWidth: 115,
            CellComponent: React.memo(
                ({
                    rowData: { clicks }
                }: GridCellProps<
                    SwitchOnAutotargetingRecommendationsItem
                >): JSX.Element => <NumberCell num={clicks} />
            )
        },
        {
            title: i18n('clicks-forecast'),
            key: SwitchOnAutotargetingKey.CLICKS_FORECAST,
            align: 'right',
            isSortable: true,
            minWidth: 115,
            CellComponent: React.memo(
                ({
                    rowData: { clicksForecast }
                }: GridCellProps<
                    SwitchOnAutotargetingRecommendationsItem
                >): JSX.Element => (
                    <ForecastValue type={ForecastValueType.positive}>
                        <NumberText
                            num={clicksForecast}
                            fractionalPartLength={0}
                        />
                    </ForecastValue>
                )
            )
        },
        {
            title: i18n('cost', {
                currency: mapStringToCurrencySymbol(currency)
            }),
            key: StatKey.STAT_COST,
            align: 'right',
            isSortable: true,
            minWidth: 120,
            CellComponent: React.memo(
                ({
                    rowData: { cost }
                }: GridCellProps<
                    SwitchOnAutotargetingRecommendationsItem
                >): JSX.Element => (
                    <MoneyCell num={cost} fractionalPartLength={0} />
                )
            )
        },
        {
            title: i18n('cost-forecast', {
                currency: mapStringToCurrencySymbol(currency)
            }),
            key: SwitchOnAutotargetingKey.COST_FORECAST,
            align: 'right',
            isSortable: true,
            minWidth: 120,
            CellComponent: React.memo(
                ({
                    rowData: { costForecast }
                }: GridCellProps<
                    SwitchOnAutotargetingRecommendationsItem
                >): JSX.Element => (
                    <ForecastValue type={ForecastValueType.neutral}>
                        <MoneyText
                            num={costForecast}
                            fractionalPartLength={0}
                        />
                    </ForecastValue>
                )
            )
        },
        {
            title: i18n('autotargeting'),
            key: SwitchOnAutotargetingKey.ACCEPT,
            isSortable: false,
            minWidth: 170,
            CellComponent: React.memo(
                ({
                    rowData: {
                        name,
                        id,
                        recommendationKeys,
                        status,
                        isApplicable
                    }
                }: GridCellProps<
                    SwitchOnAutotargetingRecommendationsItem
                >): JSX.Element => {
                    const { submitRecommendation } = actions;

                    return (
                        <RecommendationsGridItemAllowCell
                            name={name}
                            id={id}
                            isInProgress={
                                status === GdRecommendationStatus.IN_PROGRESS
                            }
                            isApplicable={isApplicable}
                            recommendationType={
                                RecommendationType.switchOnAutotargeting
                            }
                            recommendationKeys={recommendationKeys}
                            onSubmit={submitRecommendation}
                        />
                    );
                }
            )
        }
    ];
}

export function getDefaultDynamicSwitchOnAutotargetingRecommendationsColumns(): Pick<
    ColumnsSettings,
    'columnViewInfo'
> {
    return {
        columnViewInfo: {
            [SwitchOnAutotargetingKey.CAMPAIGN_ID]: {
                width: 250
            },
            [SwitchOnAutotargetingKey.NAME]: {
                width: 250
            },
            [StatKey.STAT_CLICKS]: {
                width: 115
            },
            [SwitchOnAutotargetingKey.CLICKS_FORECAST]: {
                width: 160
            },
            [StatKey.STAT_COST]: {
                width: 135
            },
            [SwitchOnAutotargetingKey.COST_FORECAST]: {
                width: 185
            },
            [SwitchOnAutotargetingKey.ACCEPT]: {
                width: 170
            }
        }
    };
}

export function getSwitchOnAutotargetingRecommendationsColumns(
    columnsSettings: ColumnsSettingsViewDict,
    actions: RecommendationsItemsScreenActions,
    login: string,
    currency: CurrencyCode
): Array<
    GridColumnDecl<
        | SwitchOnAutotargetingRecommendationsItem
        | RemovePagesFromBlackListRecommendationsItem
    >
> {
    return getStaticSwitchOnAutotargetingRecommendationsColumns(
        actions,
        login,
        currency
    ).map(column => {
        return {
            ...column,
            ...columnsSettings[RecommendationType.switchOnAutotargeting]
                .columnViewInfo[column.key]
        };
    });
}
